import { NumberInput } from '@dealstack/frontend/shared/components/NumberInput/NumberInput';
import { PerformanceRatchetTierEditStyles } from '@dealstack/frontend/shared/components/VestingTerms/PerformanceVesting/PerformanceRatchetTiers/PerformanceRatchetTiers.styles.tsx';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { PerformanceVestingMetricType } from '@dealstack/shared/lib/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import type { TierFormValues } from './PerformanceRatchetTierEdit.types';

interface Props {
	metricType: PerformanceVestingMetricType;
	initialValues?: TierFormValues;
	onSave: (values: TierFormValues) => void;
	onCancel: () => void;
}

export const PerformanceRatchetTierEdit = ({
	metricType,
	initialValues,
	onSave,
	onCancel,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const showMoic =
		metricType === PerformanceVestingMetricType.MOIC ||
		metricType === PerformanceVestingMetricType.MOIC_AND_XIRR;

	const showIrr =
		metricType === PerformanceVestingMetricType.XIRR ||
		metricType === PerformanceVestingMetricType.MOIC_AND_XIRR;

	const formShape = useMemo(
		() =>
			z.object({
				moicThreshold: showMoic
					? z
							.number({
								invalid_type_error: tCommonUi(
									'vesting_terms.error.tiers.threshold.required'
								),
								required_error: tCommonUi(
									'vesting_terms.error.tiers.threshold.required'
								),
							})
							.positive()
					: z.number().optional(),
				xirrThreshold: showIrr
					? z
							.number({
								invalid_type_error: tCommonUi(
									'vesting_terms.error.tiers.threshold.required'
								),
								required_error: tCommonUi(
									'vesting_terms.error.tiers.threshold.required'
								),
							})
							.positive()
					: z.number().optional(),
				incrementalPercentage: z
					.number({
						invalid_type_error: tCommonUi(
							'vesting_terms.error.graded.percentage.required'
						),
						required_error: tCommonUi('vesting_terms.error.graded.percentage.required'),
					})
					.positive()
					.max(100, tCommonUi('vesting_terms.error.graded.percentage.max')),
			}),
		[showMoic, showIrr, tCommonUi]
	);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<z.infer<typeof formShape>>({
		mode: 'all',
		defaultValues: {
			moicThreshold: initialValues?.moicThreshold ?? undefined,
			xirrThreshold: initialValues?.xirrThreshold ?? undefined,
			incrementalPercentage: initialValues?.incrementalPercentage ?? undefined,
		},
		resolver: zodResolver(formShape),
	});

	const onSubmit = (data: z.infer<typeof formShape>) => {
		onSave({
			moicThreshold: showMoic ? data.moicThreshold : null,
			xirrThreshold: showIrr ? data.xirrThreshold : null,
			incrementalPercentage: data.incrementalPercentage,
		});
	};

	return (
		<PerformanceRatchetTierEditStyles>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="fields-row">
					{showMoic && (
						<div className="field">
							<div className="field-label">
								{showIrr
									? tCommonUi('vesting_terms.performance.tiers.moic_threshold')
									: tCommonUi('vesting_terms.performance.tiers.threshold')}
							</div>

							<Controller
								name="moicThreshold"
								control={control}
								render={({ field }) => (
									<NumberInput
										inputPlaceholder={tCommonUi(
											'vesting_terms.performance.tiers.moic_threshold_placeholder'
										)}
										{...field}
										errorMessage={errors.moicThreshold?.message?.toString()}
									/>
								)}
							/>
						</div>
					)}

					{showIrr && (
						<div className="field">
							<div className="field-label">
								{showMoic
									? tCommonUi('vesting_terms.performance.tiers.irr_threshold')
									: tCommonUi('vesting_terms.performance.tiers.threshold')}
							</div>

							<Controller
								name="xirrThreshold"
								control={control}
								render={({ field }) => (
									<NumberInput
										inputPlaceholder={tCommonUi(
											'vesting_terms.performance.tiers.irr_threshold_placeholder'
										)}
										{...field}
										errorMessage={errors.xirrThreshold?.message?.toString()}
									/>
								)}
							/>
						</div>
					)}

					<div className="field">
						<div className="field-label">
							{tCommonUi('vesting_terms.performance.tiers.increment')}
						</div>

						<Controller
							name="incrementalPercentage"
							control={control}
							render={({ field }) => (
								<NumberInput
									inputPlaceholder={tCommonUi(
										'vesting_terms.performance.tiers.increment_placeholder'
									)}
									{...field}
									errorMessage={errors.incrementalPercentage?.message?.toString()}
								/>
							)}
						/>
					</div>
				</div>

				<div className="actions-row">
					<button type="button" className="cancel-button" onClick={onCancel}>
						<IconX stroke={2} size={24} />
						{tCommonUi('vesting_terms.cancel')}
					</button>

					<button type="submit" className="save-button">
						<IconDeviceFloppy stroke={2} size={24} />
						{tCommonUi('vesting_terms.save')}
					</button>
				</div>
			</form>
		</PerformanceRatchetTierEditStyles>
	);
};
